import { Component, EventEmitter, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';

import {
  CandidateType, CandidateTypeEdge, CompanyType, CompanyTypeEdge, MetricValueClientReasonType, MetricValueCommentType
} from '../../../../generated/graphql';
import {
  StandupDashboardServiceService
} from '../../../pm-stats-dashboard/standup-boards/standup-dashboard-service.service';
import { RelayIdService } from '../../relay-id.service';
import { RegionService } from '../../region.service';
import {
  CandidatesCommentTableComponent
} from '../../../candidates/candidates-comment-table/candidates-comment-table.component';

@Component({
  selector: 'app-reasons-dialog',
  templateUrl: './reasons-dialog.component.html',
  styleUrls: ['./reasons-dialog.component.scss']
})
export class ReasonsDialogComponent implements OnInit {
  commentsGroup: FormGroup;
  reasonsGroup: FormGroup;
  isSaving = false;
  candidatesDict: { [key: string]: CandidateType[] } = { };
  clientsDict: { [key: string]: CompanyType[] } = { };
  reasonsTitlesDict: { [key: string]: string } = { };

  metricClientReasons: MetricValueClientReasonType[] = [];
  allowedMetricGroupsToCommentReasonChange: string[] = [
    'TWV0cmljR3JvdXBUeXBlOjEw',
    'TWV0cmljR3JvdXBUeXBlOjEx',
    'TWV0cmljR3JvdXBUeXBlOjE0',
    'TWV0cmljR3JvdXBUeXBlOjEy',
    'TWV0cmljR3JvdXBUeXBlOjMw',
    'TWV0cmljR3JvdXBUeXBlOjQw'
  ];
  isReasonChangeAllowed = false;

  get comments(): FormArray { return this.commentsGroup.get('comments') as FormArray; }
  get reasons(): FormArray { return this.reasonsGroup.get('reasons') as FormArray; }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      reasonSet: MetricValueCommentType[],
      managerName: string,
      metricGroup: string,
      metricGroupId: string,
      metricValueCandidate: { firstName: string, lastName: string, livasId: number, employeeLivasId: number },
      metricValueClient: { name: string, id: string, livasId: number },
      needUpdate: EventEmitter<boolean>
    },
    private dashboardService: StandupDashboardServiceService,
    private dialogRef: MatDialogRef<ReasonsDialogComponent>,
    private fb: FormBuilder,
    public relayIdService: RelayIdService,
    public regionService: RegionService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    if (this.allowedMetricGroupsToCommentReasonChange.includes(this.data.metricGroupId)) {
      this.dashboardService.getCommentReasonsByMetricGroup(this.data.metricGroupId).subscribe(res => {
        this.metricClientReasons = res as MetricValueClientReasonType[];
        this.isReasonChangeAllowed = true;
      });
    }

    this.commentsGroup = this.fb.group({
      comments: this.fb.array([])
    });

    this.reasonsGroup = this.fb.group({
      reasons: this.fb.array([])
    });

    this.data.reasonSet.forEach(reason => {
      this.comments.push(new FormControl(reason.comment || ''));
      this.reasons.push(new FormControl(reason.metricClientReason?.id || null));
      this.reasonsTitlesDict[reason.id] = reason.metricClientReason?.title || null;
      this.candidatesDict[reason.id] = this.getCandidates(reason);
      this.clientsDict[reason.id] = this.getClients(reason);
    });
  }

  getCandidates(reason: MetricValueCommentType): CandidateType[] {
    const listFromReasons = reason.candidates.edges
      .filter((candidate: CandidateTypeEdge) => candidate.node.firstName || candidate.node.lastName)
      .map((candidate: CandidateTypeEdge) => candidate.node);

    return listFromReasons?.length
      ? listFromReasons
      : this.data.metricValueCandidate
        ? ([this.data.metricValueCandidate as CandidateType])
        : null;
  }

  getClients(reason: MetricValueCommentType): CompanyType[] {
    const listFromReasons = reason.clients.edges
      .filter((clients: CompanyTypeEdge) => clients.node.name)
      .map((clients: CompanyTypeEdge) => clients.node);

    return listFromReasons?.length
      ? listFromReasons
      : this.data.metricValueClient
        ? ([this.data.metricValueClient as CompanyType])
        : null;
  }

  openCandidatesDialog(metricValueCommentId: string) {
    this.dialog.open(CandidatesCommentTableComponent, {
      data: { id: metricValueCommentId },
      minWidth: 920
    });
  }

  saveComment(reason: MetricValueCommentType, comment: string, reasonId: string) {
    this.isSaving = true;
    this.dashboardService.updateMetricValueComment(reason.id, comment, reasonId).subscribe(
      data => {
        this.isSaving = false;
        const fromList = this.data.reasonSet.find(r => r.id === reason.id);
        if (fromList) {
          fromList.comment = comment;
          fromList.metricClientReason = { id: reasonId } as MetricValueClientReasonType;
        }
        this.data.needUpdate.emit(true);
        this.closeDialog(
          {
            ...reason,
            comment: data.data.updateMetricValueComment.metricValueComment.comment,
            metricClientReason: data.data.updateMetricValueComment.metricValueComment.metricClientReason
          },
          data.data.updateMetricValueComment.metricValueComment.metricClientReason
        );
      },
      () => {
        this.isSaving = false;
        this.closeDialog();
      }
    );
  }

  closeDialog(reason = null, metricClientReason = null) {
    this.dialogRef.close({ reason: { ...reason }, metricClientReason: { ...metricClientReason } });
  }
}
